import {html, PolymerElement} from '@polymer/polymer/polymer-element.js';
import '@cbar/cbar-shared-styles/cbar-shared-styles.js';
import '@cbar/localizemixin/LocalizeMixin.js';
import '@cbar/cbar-address-autocomplete/cbar-address-autocomplete.js';
import '@polymer/paper-spinner/paper-spinner.js';
import '@polymer/paper-button/paper-button.js';
import '@cbar/cbar-textfield/cbar-textfield.js';
import '@cbar/cbar-dropdown/cbar-dropdown.js';
import '@material/web/dialog/dialog.js';

/**
 * `cbar-add-group-dialog`
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarAddGroupDialog extends Cbar.LocalizeMixin(PolymerElement) {
  static get template() {
    return html`
        <style include="cbar-shared-styles">
            :host {
                display: block;
                width: 100%;
            }

            md-dialog {
                width: 100%;
                max-width: 600px;
                overflow: visible;
            }

            cbar-textfield, cbar-dropdown {    
                --mdc-theme-primary: var(--cbar-sys-color-primary, #004CA2);
                width: 100%;
            }

            .routing-message {
                white-space: break-spaces;
            }
        </style>
        <md-dialog id="new-form-dialog" with-backdrop="true" no-cancel-on-outside-click no-cancel-on-esc-key allow-click-through open="{{opened}}" on-close="__closeHandler">
            <h6 style="margin-bottom: 0" slot="headline">[[localize(dialogTitle)]]</h6>

            <template is="dom-if" if="[[loggedIn]]">
                <div slot="content">
                    <template is="dom-if" if="[[__subscriptionMessage]]">
                        <div style="margin-bottom: 16px">[[__subscriptionMessage]]</div>
                    </template>
                    <cbar-textfield label="Title" dense class="form-input" validationMessage="Required" required id="locale-title" value="[[title]]" on-input="__titleEdited"></cbar-textfield>
                    <template is="dom-if" if="[[__showGroupSelect]]">
                        <cbar-dropdown items="[[_parentGroups]]" dense label="Sub-group of" fixedMenuPosition style="margin-bottom:16px" on-change="__handleParentChange"></cbar-dropdown>
                    </template>
                    <template is="dom-if" if="[[withAddress]]">
                        <cbar-address-autocomplete api-key="[[apiKey]]" street-address="{{streetAddress}}" street-address2="{{streetAddress2}}" street-address3="{{streetAddress3}}" state="{{state}}" suburb="{{suburb}}" postcode="{{postcode}}" country="{{country}}" title="{{locationTitle}}" optional-fields="[[optionalFields]]"></cbar-address-autocomplete>
                    </template>
                </div>


                <div class="buttons flex-buttons" style="display: flex; align-items: center" slot="actions">
                    <template is="dom-if" if="[[formError]]">
                        <span class="form-error" style="color: red">[[formError]]</span>
                    </template>
                    <div style="display: flex; align-items: center">
                        <paper-spinner active="[[formLoading]]"></paper-spinner>
                        <paper-button on-tap="__handleCancel" disabled$="[[formLoading]]">[[localize('Cancel')]]
                        </paper-button>
                        <paper-button on-tap="confirmNewLocation" disabled$="[[formLoading]]" id="submit-button">
                            [[localize('Submit')]]
                        </paper-button>
                    </div>
                </div>
            </template>


            <template is="dom-if" if="[[!loggedIn]]">
                <div slot="content">[[localize('Please log in or sign up to continue.')]]</div>

                <div class="buttons" slot="actions">
                    <paper-spinner active="[[formLoading]]"></paper-spinner>
                    <paper-button dialog-dismiss disabled$="[[formLoading]]">[[localize('Cancel')]]</paper-button>
                    <paper-button on-tap="confirmLoggingIn" disabled$="[[formLoading]]">[[localize('Login')]]
                    </paper-button>
                </div>
            </template>
        </md-dialog>

        <md-dialog id="newLocaleConfirmDialog" with-backdrop="true" scroll-action="lock" open="{{confirmed}}">
            <h6>[[localize(confirmTitle)]]</h6>
            <div>[[confirmationMessage]]</div>
            <div class="buttons">
                <template is="dom-if" if="[[!confirmLoading]]">
                    <paper-button dialog-dismiss>[[localize('Close')]]</paper-button>
                </template>
                <template is="dom-if" if="[[confirmLoading]]">
                    <paper-spinner active></paper-spinner>
                </template>
            </div>
        </md-dialog>

        <md-dialog id="newLocaleRoutingDialog" with-backdrop="true" scroll-action="lock" open="{{routing}}" no-cancel-on-outside-click no-cancel-on-esc-key>
            <h6>[[localize(routingTitle)]]</h6>
            <div class="routing-message">[[routingMessage]]</div>
            <div class="buttons">
                <paper-button on-tap="confirmRoute">[[localize('Continue')]]</paper-button>
            </div>
        </md-dialog>
    `;
  }

  static get properties() {
    return {
      loggedIn: {
        type: Boolean,
        value: false,
        observer: '__resizeDialog'
      },
      opened: {
        type: Boolean,
        value: false
      },
      apiKey: String,
      confirmed: {
        type: Boolean,
        value: false
      },
      formLoading: {
        type: Boolean,
        value: false
      },
      title: {
        type: String,
        notify: true
      },
      streetAddress: {
        type: String,
        notify: true
      },
      streetAddress2: {
        type: String,
        notify: true,
      },
      streetAddress3: {
        type: String,
        notify: true,
      },
      state: {
        type: String,
        notify: true
      },
      suburb: {
        type: String,
        notify: true
      },
      postcode: {
        type: String,
        notify: true
      },
      country: {
        type: String,
        notify: true
      },
      locationTitle: {
        type: String,
        notify: true
      },
      optionalFields: {
        type: Array,
        value: [
          'title',
          'address2',
          'address3'
        ]
      },
      confirmationMessage: {
        type: String,
        value: 'Thanks for submitting your group, we\'ll be in touch!'
      },
      withAddress: {
        type: Boolean,
        value: false
      },
      dialogTitle: {
        type: String,
        value: 'Add Details'
      },
      confirmTitle: {
        type: String,
        value: 'Confirmed!'
      },
      routingTitle: {
        type: String,
        value: 'One more step…'
      },
      parentGroups: {
        type: Array,
        value: null,
        observer: '__handleParentGroups'
      },
      subscriptionMessage: {
        type: String,
        value: null
      },
      subscriptionPrice: {
        type: String,
        value: false
      },
      routingMessage: {
        type: String,
        value: 'Thanks for providing your details.\n\nNow the fun part to create your profile page!'
      }
    };
  }

  static get observers() {
    return [
      '__handleSubscriptionMessage(subscriptionMessage, subscriptionPrice)'
    ];
  }

  /**
   * Returns the `md-dialog` instance in the component's shadow DOM.
   *
   * @returns {HTMLElement} The dialog element.
   */
  get dialog() {
    if (!this._dialog) {
      this._dialog = this.root.querySelector('md-dialog');
    }
    return this._dialog;
  }

  ready() {
    super.ready();
    requestAnimationFrame(() => this.handleOverflowIssue());
  }

  get newLocaleForm() {
    if (!this.__newLocaleForm) {
      this.__newLocaleForm = this.root.querySelector('cbar-address-autocomplete');
    }
    return this.__newLocaleForm;
  }

  get newformDialog() {
    if (!this.__formDialog) {
      this.__formDialog = this.root.querySelector('#new-form-dialog');
    }
    return this.__formDialog;
  }

  open() {
    this.opened = true;
    requestAnimationFrame(() => this.handleOverflowIssue());
  }

  reset() {
    if (this.withAddress) {
      this.resetAddress();
    }

    this.title = '';
    this.formError = false; // Reset form error
    this.formLoading = false; // Ensure loading state is cleared
  }

  resetAddress() {
    let titleInput = this.root.querySelector('#locale-title');
    let titleInputRequired = titleInput.required;
    this.formLoading = false;
    this.newLocaleForm.setProperties({
      lookupAddress: '',
      title: '',
      streetAddress: '',
      streetAddress2: '',
      streetAddress3: '',
      suburb: '',
      state: '',
      postcode: '',
      country: ''
    });

    this.newLocaleForm.resetValidity();

    titleInput.value = '';
    titleInput.required = false;
    titleInput.validationMessage = '';

    requestAnimationFrame(() => {
      titleInput.required = titleInputRequired;
    });
  }

  confirmNewLocation() {
    if (!this.validate()) {
      return;
    }

    this.formLoading = true;

    this.dispatchEvent(new CustomEvent('confirm-location', {
      bubbles: true,
      composed: true
    }));
  }

  validate() {
    let formIsValid = true;
    if (this.withAddress && !this.newLocaleForm.validate()) {
      this.newLocaleForm.scrollIntoView();
      formIsValid = false;
    }

    //NOTE: we only currently have 1 input here, but for future proofing we have added a loop
    // this also helps with null detecting
    let validateInputs = this.root.querySelectorAll('.form-input');
    let invalidCount = 0;
    validateInputs.forEach(input => {
      if (!input.reportValidity()) {
        // when an element is picked up as invalid, scroll to the first one found to be invalid
        if (invalidCount === 0) {
          input.scrollIntoView();
          invalidCount++;
        }
        formIsValid = false;
        input.validationMessage = input.value.length === 0 ? 'Required' : 'Invalid';
      }
    });

    return formIsValid;
  }

  confirmLoggingIn() {
    this.dispatchEvent(new CustomEvent('login', {
      bubbles: true,
      composed: true
    }));
  }

  __resizeDialog() {
    requestAnimationFrame(() => {
      this.formLoading = false;
    });
  }

  __titleEdited(event) {
    this.title = event.target.value;
  }

  __handleCancel() {
    this.reset();
    this.opened = false;
    this.formError = false;
  }

  __handleParentGroups(list) {
    if(this._removingFromAttribute && !list){
      this._removingFromAttribute = false;
      return;
    }

    this.__showGroupSelect = !!list && list.length > 0;
    this._parentGroups = list;

    this._removingFromAttribute = true;
    this.removeAttribute('parent-groups');
  }

  __handleSubscriptionMessage(subscriptionMessage, subscriptionPrice) {
    if (!subscriptionMessage) {
      return;
    }
    this.__subscriptionMessage = `${subscriptionMessage} ${subscriptionPrice ? subscriptionPrice : ''}`;
  }

  __handleParentChange(e) {
    let value = e.target.value;

    if (!value) {
      return;
    }

    this.selectedParentGroup = value;
  }

  confirmRoute() {
    this.dispatchEvent(new CustomEvent('routing', {
      bubbles: true,
      composed: true
    }));
  }

  __closeHandler(e){
    if(this.formLoading){
      e.preventDefault();
    }else{
      this.opened = false;
    }
  }

  /**
   * Fixes overflow issues for the dialog container and scroller.
   */
  handleOverflowIssue() {
    this.dialog.container.style.overflow = 'visible';
    this.dialog.scroller.style.overflow = 'visible';
  }

}

window.customElements.define('cbar-add-group-dialog', CbarAddGroupDialog);
